import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { SignaturePriceSummary } from './SignaturePriceSummary';
import {
  buildSignatureSummary,
  formatDateIt,
  formatEuro,
  resolvePlaceholders,
  type SignatureSummary,
  type SummaryLine,
} from './signatureSummary';

interface DealContentProps {
  deal: any;
  dealServices: any[];
  /** Dati dell'azienda che propone (RPC firma, campo company). */
  company?: any;
  expiresAt?: string | null;
  notifyEmail?: string | null;
  /** Scheda del contratto, mostrata dopo i servizi. */
  contractSlot?: React.ReactNode;
  summary?: SignatureSummary;
}

const card = 'bg-white rounded-2xl border border-black/[0.08] shadow-sm overflow-hidden';
const label = 'text-[11px] font-semibold uppercase tracking-wide text-[#6B6762]';

/**
 * Contenuto dell'offerta nella pagina di firma, mobile-first:
 * chi propone e a chi, quanto costa (una tantum separata dai canoni), rate,
 * voci divise per tipo, contratto, cosa succede dopo la firma.
 */
export function DealContent({ deal, dealServices, company, expiresAt, notifyEmail, contractSlot, summary }: DealContentProps) {
  const s = useMemo(() => summary ?? buildSignatureSummary(dealServices), [summary, dealServices]);
  const client = deal?.client;
  const ctx = { client, deal, company };
  const description = resolvePlaceholders(deal?.description, ctx);
  const expires = formatDateIt(expiresAt);
  const firstInstallment = s.installments[0];

  return (
    <div className="space-y-4">
      <section className={card}>
        <div className="px-6 sm:px-8 py-6">
          <div className={label}>
            Offerta{deal?.quote_number ? ` ${deal.quote_number}` : ''}
            {company?.business_name ? ` · da ${company.business_name}` : ''}
          </div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-bold text-[#37352F] leading-tight break-words">
            {deal?.title ?? 'Proposta'}
          </h1>
          {client?.business_name && (
            <p className="mt-2 text-sm text-[#6B6762]">
              Per <span className="font-semibold text-[#37352F]">{client.business_name}</span>
              {client.vat_number ? ` · P.IVA ${client.vat_number}` : ''}
            </p>
          )}
          {expires && (
            <span className="mt-3 inline-flex rounded-full bg-[#F7F6F3] px-3 py-1 text-xs text-[#6B6762]">
              Link valido fino al {expires}
            </span>
          )}
        </div>
        {description && (
          <div className="px-6 sm:px-8 py-4 border-t border-black/[0.06]">
            <div
              className="prose prose-sm max-w-none text-[#6B6762]"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        )}
      </section>

      <SignaturePriceSummary summary={s} />

      <section className={card}>
        <ServiceGroup title={s.monthly.length || s.annual.length ? 'Progetto · una tantum' : 'Cosa comprende'} lines={s.oneTime} ctx={ctx} first />
        <ServiceGroup title="Ogni mese" lines={s.monthly} ctx={ctx} suffix="/mese" />
        <ServiceGroup title="Ogni anno" lines={s.annual} ctx={ctx} suffix="/anno" />
      </section>

      {contractSlot}

      {(notifyEmail || firstInstallment) && (
        <section className={card}>
          <div className="px-6 sm:px-8 py-5">
            <div className={label}>Dopo la firma</div>
            <ol className="mt-3 space-y-2 text-sm text-[#37352F]">
              {notifyEmail && <Step n={1}>Ricevi il contratto firmato a {notifyEmail}</Step>}
              {firstInstallment && (
                <Step n={notifyEmail ? 2 : 1}>
                  Prima rata: {firstInstallment.label.toLowerCase()}, {formatEuro(firstInstallment.amount)} + IVA, a ricevimento fattura
                </Step>
              )}
            </ol>
          </div>
        </section>
      )}

      {company?.terms_and_conditions && <Terms html={company.terms_and_conditions} />}

      {company?.business_name && (
        <footer className="px-2 text-xs text-[#9D9892]">
          {company.business_name}
          {company.street_address ? ` · ${company.street_address} ${company.street_number ?? ''}` : ''}
          {company.city ? `, ${company.postal_code ?? ''} ${company.city}${company.province ? ` (${company.province})` : ''}` : ''}
          {company.vat_number ? ` · P.IVA ${company.vat_number}` : ''}
        </footer>
      )}
    </div>
  );
}

function ServiceGroup({ title, lines, ctx, suffix, first }: { title: string; lines: SummaryLine[]; ctx: any; suffix?: string; first?: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!lines.length) return null;
  return (
    <div className={first ? '' : 'border-t border-black/[0.06]'}>
      <div className="px-6 sm:px-8 pt-4 pb-2 flex justify-between text-xs text-[#6B6762]">
        <span>{title}</span>
        <span>{lines.length} {lines.length === 1 ? 'voce' : 'voci'}</span>
      </div>
      <ul className="divide-y divide-black/[0.05]">
        {lines.map((l) => {
          const isOpen = open === l.id;
          const desc = l.description ? resolvePlaceholders(l.description, ctx) : '';
          return (
            <li key={l.id}>
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => desc && setOpen(isOpen ? null : l.id)}
                className="w-full px-6 sm:px-8 py-3.5 text-left flex items-start justify-between gap-4 hover:bg-black/[0.015]"
              >
                <span className="font-medium text-[#37352F]">{l.title}</span>
                <span className="shrink-0 flex items-center gap-1.5 text-right">
                  <span>
                    {l.price !== l.fullPrice && (
                      <span className="block text-xs text-[#9D9892] line-through tabular-nums">{formatEuro(l.fullPrice)}</span>
                    )}
                    <span className="font-semibold text-[#37352F] tabular-nums whitespace-nowrap">
                      {formatEuro(l.price)}
                      {suffix && <span className="font-normal text-xs text-[#9D9892]"> {suffix}</span>}
                    </span>
                  </span>
                  {desc && <ChevronDown className={`h-4 w-4 text-[#9D9892] transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
                </span>
              </button>
              {isOpen && desc && (
                <div className="px-6 sm:px-8 pb-4">
                  <div className="prose prose-sm max-w-none text-sm text-[#6B6762]" dangerouslySetInnerHTML={{ __html: desc }} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F7F6F3] text-xs font-semibold">{n}</span>
      <span className="pt-0.5">{children}</span>
    </li>
  );
}

function Terms({ html }: { html: string }) {
  const [open, setOpen] = useState(false);
  return (
    <section className={card}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full px-6 sm:px-8 py-4 flex items-center justify-between">
        <span className="font-semibold text-[#37352F]">Termini e condizioni</span>
        <ChevronDown className={`h-5 w-5 text-[#9D9892] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-6 sm:px-8 pb-6 border-t border-black/[0.06]">
          <div className="prose prose-sm max-w-none max-h-96 overflow-y-auto pt-3" dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      )}
    </section>
  );
}
